"use client";
import { JSX, useRef } from "react";
import { exo2SemiBold, exo2Medium, montserratRegular } from "@@/utils/fonts";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import styles from "@@/styles/components/shared/UserCardList.module.scss";
import { TeamUserApi } from "@@/utils/getTeam";
import { TeamSectionGroup } from "@@/app/[locale]/about/page";

type UserWithLocalizedFields = TeamUserApi & {
    Role?: string;
    SignaturePhrase?: string;
};

interface Props {
    title: string;
    sections?: TeamSectionGroup[];
    users?: UserWithLocalizedFields[];
    sectionId?: string;
    className?: string;
    userCardClassName?: string;
}

const formatSectionLabel = (section?: string) => {
    if (!section) return "";
    return section.charAt(0).toUpperCase() + section.slice(1);
};

type AnimatedUserCardProps = {
    user: UserWithLocalizedFields;
    index: number;
    className?: string;
};

function AnimatedUserCard({ user, index, className }: AnimatedUserCardProps) {
    const animation = useAppearOnScroll<HTMLLIElement>({
        from: "fromBottom",
        duration: 500,
        delay: index * 100,
        offset: 200,
    });
    
    return (
        <li
            ref={animation.ref}
            className={`duration-500 ease-in-out ${styles.userContainer} ${animation.className} ${className} h-fit`}
            style={animation.style}
        >
            {user.Picture && (
                <img
                    src={user.Picture.url}
                    className="position-relative overflow-hidden rounded-1 flex-1 w-100 object-cover"
                    width={user.Picture.width}
                    height={user.Picture.height}
                    alt={`${user.Firstname} ${user.Lastname}`}
                />
            )}
            <div className="d-flex flex-column gap-1">
                <h4
                    className={`${exo2SemiBold.className} ${styles.userName}`}
                >{`${user.Firstname} ${user.Lastname}`}</h4>
                {user.Role && (
                    <h5 className={`${exo2Medium.className} fs-0_8 fw-medium`}>{user.Role}</h5>
                )}
                {user.SignaturePhrase && (
                    <p className={`${montserratRegular.className} text-wrap`}>
                        {user.SignaturePhrase}
                    </p>
                )}
            </div>
        </li>
    );
}

interface SectionBlockProps {
    sectionKey: string;
    users: UserWithLocalizedFields[];
    userCardClassName?: string;
}

function SectionBlock({
                          sectionKey,
                          users,
                          userCardClassName,
                      }: SectionBlockProps) {
    const sectionTitleAnim = useAppearOnScroll<HTMLHeadingElement>({
        from: "fromBottom",
        duration: 600,
        delay: 0,
    });
    
    return (
        <div
            className={`d-flex flex-column gap-4 flex-1 min-h-fit ${styles.sectionBlock ?? ""}`}
            style={{minWidth: ["Visual", "Technical"].includes(formatSectionLabel(sectionKey)) ? '100%' : 'calc(((((100vw - 22.8vw) - 10rem) / 4) - 4rem) * 2)'}}
        >
            <h3
                ref={sectionTitleAnim.ref}
                className={`${exo2SemiBold.className} ${
                    styles.sectionTitle ?? ""
                } ${sectionTitleAnim.className} fs-2`}
                style={sectionTitleAnim.style}
            >
                {formatSectionLabel(sectionKey)}
            </h3>
            <ul className={styles.userListContainer}>
                {users.map((user, index) => (
                    <AnimatedUserCard
                        key={user.documentId ?? `${sectionKey}-${index}`}
                        user={user}
                        index={index}
                        className={`${userCardClassName} mx-w-25-mobile`}
                    />
                ))}
            </ul>
        </div>
    );
}

export default function UserCardList({
                                         title,
                                         sections,
                                         users,
                                         sectionId,
                                         className,
                                         userCardClassName,
                                     }: Props): JSX.Element {
    const titleAnim = useAppearOnScroll<HTMLHeadingElement>({
        from: "fromBottom",
        duration: 800,
        delay: 0,
    });
    const animatedBg = useAppearOnScroll<HTMLElement>({
        from: "fadeIn",
        duration: 800,
        delay: 0,
        offset: 150,
    });
    
    const sectionRef = useRef<HTMLElement | null>(null);
    
    const hasSections = Array.isArray(sections) && sections.length > 0;
    
    return (
        <section
            id={sectionId}
            ref={sectionRef}
            className={`px-11 position-relative color-white ${className}`}
        >
            <span
                ref={animatedBg.ref}
                className={`${animatedBg.className} blue-bg`}
                style={animatedBg.style}
            ></span>
            <div className="w-100 py-10 p-5 d-flex flex-column gap-4 rounded-1 overflow-hidden">
                <h2
                    ref={titleAnim.ref}
                    className={`${exo2SemiBold.className} ${titleAnim.className}`}
                    style={titleAnim.style}
                    dangerouslySetInnerHTML={{ __html: title }}
                ></h2>
                
                {/* Wrapper des sections : flex-wrap de sections */}
                {hasSections ? (
                    <div
                        className={`d-flex flex-wrap gap-4 position-relative ${
                            styles.sectionsWrapper ?? ""
                        }`}
                    >
                        {sections!.map((group) => (
                            <SectionBlock
                                key={group.section}
                                sectionKey={group.section}
                                users={group.users as UserWithLocalizedFields[]}
                                userCardClassName={userCardClassName}
                            />
                        ))}
                    </div>
                ) : (
                    <ul className={styles.userListContainer}>
                        {(users ?? []).map((user, index) => (
                            <AnimatedUserCard
                                key={user.documentId ?? index}
                                user={user}
                                index={index}
                                className={userCardClassName}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
